import { ACCOUNT_STATUS_VALUES, CONTEXT_SOURCE_VALUES, type AccountStatus, type ContextSource } from "@sagot/constants";
import type { components } from "@sagot/api-client";

type Schemas = components["schemas"];

export type CustomerContext = Schemas["CustomerContextOut"];
export type CustomerContextItem = Schemas["CustomerContextItemOut"];
export type CustomerField = Schemas["CustomerFieldOut"];
export type CustomerTier = NonNullable<CustomerContext["tier"]>;
export type CustomerLookup = Schemas["CustomerLookupItemOut"];
export type CustomerPointer = Schemas["CustomerPointer"];

/** A change reaches the open card within this (NFR-05 asks for 5 s; the inbox stream usually beats it). */
export const CONTEXT_POLL_MS = 4_000;
/** One list call may ask about this many contacts (contract E31). */
export const CONTEXT_BATCH_MAX = 100;
/** A revealed reference hides itself again after this long. */
export const REVEAL_VISIBLE_MS = 30_000;
/** Custom-field columns the contacts table shows next to tier and status. */
export const CONTACT_FIELD_COLUMNS_MAX = 2;

export function isAccountStatus(value: string | null): value is AccountStatus {
  return ACCOUNT_STATUS_VALUES.some((status) => status === value);
}

export function isContextSource(value: string | null): value is ContextSource {
  return CONTEXT_SOURCE_VALUES.some((source) => source === value);
}

/** The tier's label in the interface language, else English, else its code. */
export function tierLabel(tier: CustomerTier, language: string): string {
  const labels: Record<string, string> = tier.labels;
  return labels[language] ?? tier.label;
}

/** How a status reads as a tone: the standing at the operator, never by colour alone (the text is always shown). */
export function statusTone(status: string | null): "success" | "warning" | "danger" | "neutral" {
  if (status === "active") return "success";
  if (status === "restricted" || status === "kyc_pending") return "warning";
  if (status === "suspended" || status === "self_excluded" || status === "closed") return "danger";
  return "neutral";
}

/** Splits ids into the chunks one E31 call may carry. */
export function chunkIds(ids: readonly string[], size: number = CONTEXT_BATCH_MAX): string[][] {
  const chunks: string[][] = [];
  for (let index = 0; index < ids.length; index += size) chunks.push(ids.slice(index, index + size));
  return chunks;
}

/** The fields the contacts table shows as columns: the first ones the loaded customers carry, in the order met. */
export function columnFields(items: readonly (CustomerContextItem | null | undefined)[], max: number = CONTACT_FIELD_COLUMNS_MAX): { key: string; label: string }[] {
  const seen = new Map<string, string>();
  for (const item of items) {
    for (const field of item?.fields ?? []) {
      if (!seen.has(field.key) && seen.size < max) seen.set(field.key, field.label);
    }
  }
  return [...seen].map(([key, label]) => ({ key, label }));
}
